import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { publicApi } from "../lib/api";
import { toast } from "../hooks/use-toast";
import { ArrowRight, KeyRound, Mail } from "lucide-react";
import { useEffect } from "react";

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true); try { await publicApi.post('/member/forgot-password', { email }); setSent(true); } catch { setSent(true); } finally { setBusy(false); } };
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <form onSubmit={submit} className="w-full max-w-[440px] rounded-lg p-8" style={{ background: '#fbf8ef' }}>
        <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4" style={{ background: '#0f3d2e', color: '#f4efe4' }}><Mail size={18} /></div>
        <h1 className="hero-title text-[36px] leading-tight mb-2">Forgot your <span className="font-serif-i text-[#e0684d]">password</span>?</h1>
        {sent ? (
          <p className="text-[14px] text-[#0d2a20]/80">If an account exists for that email, a reset link is on its way. Please check your inbox (and spam folder). The link expires in 60 minutes.</p>
        ) : (
          <>
            <p className="text-[13px] text-[#0d2a20]/70 mb-6">Enter your email and we'll send you a secure reset link.</p>
            <label className="text-[12px] text-[#0d2a20]/70">Email</label>
            <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full mt-1 mb-4 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
            <button disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px] disabled:opacity-60">{busy ? 'Sending…' : 'Send reset link'} <ArrowRight size={14} /></button>
          </>
        )}
        <div className="mt-6 text-[13px]"><Link to="/login" className="link-underline text-[#0d2a20]/80">Back to sign in</Link></div>
      </form>
    </div>
  );
};

export const ResetPassword = () => {
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { publicApi.get(`/member/reset-password/${token}`).catch(err => setError(err?.response?.data?.detail || 'Reset link invalid or expired')); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast({ title: 'Too short', description: 'Use at least 8 characters.' });
    if (password !== confirm) return toast({ title: 'Passwords do not match' });
    setBusy(true);
    try { await publicApi.post(`/member/reset-password/${token}`, { password }); toast({ title: 'Password updated', description: 'You can now sign in.' }); nav('/login', { replace: true }); }
    catch (err) { setError(err?.response?.data?.detail || 'Could not reset'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <form onSubmit={submit} className="w-full max-w-[440px] rounded-lg p-8" style={{ background: '#fbf8ef' }}>
        <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4" style={{ background: '#0f3d2e', color: '#f4efe4' }}><KeyRound size={18} /></div>
        <h1 className="hero-title text-[36px] leading-tight mb-2">Choose a new <span className="font-serif-i text-[#e0684d]">password</span></h1>
        {error ? (
          <div className="text-[14px] text-[#7a1f13] bg-[#f3d6d1] rounded-md p-3">{error} <div className="mt-2"><Link to="/forgot-password" className="link-underline">Request a new link</Link></div></div>
        ) : (
          <>
            <p className="text-[13px] text-[#0d2a20]/70 mb-6">Minimum 8 characters.</p>
            <label className="text-[12px] text-[#0d2a20]/70">New password</label>
            <input required type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full mt-1 mb-3 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
            <label className="text-[12px] text-[#0d2a20]/70">Confirm password</label>
            <input required type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="w-full mt-1 mb-4 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
            <button disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px] disabled:opacity-60">{busy ? 'Updating…' : 'Update password'} <ArrowRight size={14} /></button>
          </>
        )}
      </form>
    </div>
  );
};
